// src/pages/ComponentsTestPage.tsx
// Página para probar los componentes migrados de la Fase 2

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ImageIcon, Star, Bell } from 'lucide-react';
import logoCorge from '../assets/logos/logo-corgee.png';

// Componentes UI
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, ConfirmDialog } from '../components/ui/Dialog';
import { useToast } from '../components/ui/Toast';

// Componentes migrados
/*
import { ImageViewer } from '../components/ImageViewer/ImageViewer';
import { CopyButton, CopyInput, CopyCode } from '../components/CopyButton/CopyButton';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { FileUpload } from '../components/FileUpload';
import type { FileAttachment } from '../hooks/useFileUpload';
import { ActionMenu, ConversationActionMenu, ReplyActionMenu } from '../components/ActionMenu';
import { useUndoToast, UndoDeleteToast } from '../components/UndoToast';
import { ImageGallery, type GalleryImage } from '../components/ImageGallery';
import { FilterBar, useFilters, type FilterOption } from '../components/FilterBar';
import { ReminderPicker, useReminders, type Reminder } from '../components/ReminderPicker';
*/

// Stubs for commented-out imports
type FileAttachment = any;
type GalleryImage = any;
type FilterOption = any;
const useUndoToast: any = () => ({ showUndoToast: (_: any) => {} });
const useFilters: any = () => ({
  filters: [],
  addFilter: (_: any) => {},
  removeFilter: (_: any) => {},
});
const useReminders: any = (_: any) => ({
  reminders: [],
  removeReminder: (_2: any) => {},
  removeAll: () => {},
});
const useInfiniteScroll: any = (_: any) => ({});
const ImageViewer: any = () => null;
const CopyButton: any = () => null;
const CopyInput: any = () => null;
const CopyCode: any = () => null;
const FileUpload: any = () => null;
const ActionMenu: any = () => null;
const ConversationActionMenu: any = () => null;
const ReplyActionMenu: any = () => null;
const UndoDeleteToast: any = () => null;
const ImageGallery: any = () => null;
const FilterBar: any = () => null;
const ReminderPicker: any = () => null;

export const ComponentsTestPage: React.FC = () =>
{
  const toast = useToast();
  const { showUndoToast } = useUndoToast();
  const { filters, addFilter: _addFilter, removeFilter: _removeFilter } = useFilters();
  const { reminders, removeReminder, removeAll } = useReminders([
    {
      id: 1,
      Title: 'Team Meeting',
      Body: 'Quarterly review meeting with the development team',
      NextReminderDateUTC: new Date(Date.now() + 3600000).toISOString(), // 1 hour from now
    },
    {
      id: 2,
      Title: 'Project Deadline',
      Body: 'Submit final project deliverables',
      NextReminderDateUTC: new Date(Date.now() + 86400000).toISOString(), // 1 day from now
    },
    {
      id: 3,
      Title: 'Code Review',
      Body: 'Review pull requests from team members',
      NextReminderDateUTC: new Date(Date.now() - 1800000).toISOString(), // 30 min ago
    },
  ]);

  // Estados para Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Estados para ImageViewer
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // Estados para Infinite Scroll
  const [items, setItems] = useState<number[]>(Array.from({ length: 20 }, (_, i) => i + 1));
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Estados para FileUpload
  const [uploadedFiles, setUploadedFiles] = useState<FileAttachment[]>([]);

  // Estados para UndoToast
  const [undoToastVisible, setUndoToastVisible] = useState(false);
  const [deletedItem, setDeletedItem] = useState<string | null>(null);

  // Estados para ImageGallery
  const [galleryOpen, setGalleryOpen] = useState(false);

  // Estados para ReminderPicker
  const [reminderPickerOpen, setReminderPickerOpen] = useState(false);

  // Estados para FilterBar
  const [_searchText, setSearchText] = useState('');
  const [galleryImages, setGalleryImages] = useState<GalleryImage[]>([
    {
      AttachmentId: 1,
      Url: 'https://picsum.photos/800/600?random=1',
      FileName: 'landscape-1.jpg',
      Description: 'Beautiful landscape',
      Favorite: true,
    },
    {
      AttachmentId: 2,
      Url: 'https://picsum.photos/800/600?random=2',
      FileName: 'landscape-2.jpg',
      Description: 'Mountain view',
      Favorite: false,
    },
    {
      AttachmentId: 3,
      Url: 'https://picsum.photos/800/600?random=3',
      FileName: 'landscape-3.jpg',
      Description: 'Beach sunset',
      Favorite: false,
    },
    {
      AttachmentId: 4,
      Url: 'https://picsum.photos/800/600?random=4',
      FileName: 'landscape-4.jpg',
      Description: 'Forest path',
      Favorite: true,
    },
    {
      AttachmentId: 5,
      Url: 'https://picsum.photos/800/600?random=5',
      FileName: 'landscape-5.jpg',
      Description: 'City lights',
      Favorite: false,
    },
    {
      AttachmentId: 6,
      Url: 'https://picsum.photos/800/600?random=6',
      FileName: 'landscape-6.jpg',
      Description: 'Desert dunes',
      Favorite: false,
    },
  ]);

  // Opciones de filtro de ejemplo
  const sampleFilterOptions: FilterOption[] = [
    { id: '1', name: 'Status: Active', fieldName: 'status', value: 'active' },
    { id: '2', name: 'Status: Pending', fieldName: 'status', value: 'pending' },
    { id: '3', name: 'Priority: High', fieldName: 'priority', value: 'high' },
    { id: '4', name: 'Priority: Medium', fieldName: 'priority', value: 'medium' },
    { id: '5', name: 'Priority: Low', fieldName: 'priority', value: 'low' },
    { id: '6', name: 'Type: Bug', fieldName: 'type', value: 'bug' },
    { id: '7', name: 'Type: Feature', fieldName: 'type', value: 'feature' },
    { id: '8', name: 'Type: Task', fieldName: 'type', value: 'task' },
    { id: '9', name: 'Assignee: Me', fieldName: 'assignee', value: 'me' },
    { id: '10', name: 'Due: Today', fieldName: 'due', value: 'today' },
  ];

  // Imágenes de prueba
  const sampleImages = [
    {
      AttachmentId: 1,
      Url: 'https://picsum.photos/800/600?random=1',
      FileName: 'Imagen 1.jpg',
    },
    {
      AttachmentId: 2,
      Url: 'https://picsum.photos/800/600?random=2',
      FileName: 'Imagen 2.jpg',
    },
    {
      AttachmentId: 3,
      Url: 'https://picsum.photos/800/600?random=3',
      FileName: 'Imagen 3.jpg',
    },
    {
      AttachmentId: 4,
      Url: 'https://picsum.photos/800/600?random=4',
      FileName: 'Imagen 4.jpg',
    },
  ];

  // Código de ejemplo para CopyCode
  const sampleCode = `import React from 'react';
import { Button } from './components/ui/Button';

export const Example = () => {
  return (
    <Button variant="default">
      Click me!
    </Button>
  );
};`;

  // Handler para infinite scroll
  const loadMoreItems = async () =>
  {
    setIsLoadingMore(true);
    await new Promise(resolve => setTimeout(resolve, 1000)); // Simular carga
    setItems(prev => [...prev, ...Array.from({ length: 10 }, (_, i) => prev.length + i + 1)]);
    setIsLoadingMore(false);
  };

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: loadMoreItems,
    enabled: !isLoadingMore,
    threshold: 200,
  });

  // Handlers para toasts
  const showSuccessToast = () =>
  {
    toast.success('Operación exitosa!', 'Éxito');
  };

  const showErrorToast = () =>
  {
    toast.error('Algo salió mal', 'Error');
  };

  const showWarningToast = () =>
  {
    toast.warning('Ten cuidado con esto', 'Advertencia');
  };

  const showInfoToast = () =>
  {
    toast.info('Esta es información importante', 'Info');
  };

  const showActionToast = () =>
  {
    toast.addToast({
      type: 'info',
      message: 'Elemento eliminado',
      action: {
        label: 'Deshacer',
        onClick: () =>
        {
          toast.success('Acción deshecha!');
        },
      },
      duration: 5000,
    });
  };

  return (
    <div className="min-h-screen bg-[#ededed]">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={logoCorge} alt="Corgee Logo" className="w-10 h-10 object-contain" />
            <div>
              <h1 className="text-2xl font-semibold text-[#5dade2]">Test de Componentes</h1>
              <p className="text-sm text-gray-500">Fase 2 - Componentes Migrados</p>
            </div>
          </div>

          <Link to="/dashboard">
            <Button variant="outline">
              <ArrowLeft className="w-4 h-4" />
              Volver al Dashboard
            </Button>
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* UI Base Components */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-sm font-bold">
              1
            </span>
            Componentes Base UI
          </h2>

          <div className="space-y-6">
            {/* Buttons */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Buttons</h3>
              <div className="flex flex-wrap gap-2">
                <Button variant="default">Primary</Button>
                <Button variant="secondary">Secondary</Button>
                <Button variant="outline">Outline</Button>
                <Button variant="ghost">Ghost</Button>
                <Button variant="destructive">Danger</Button>
                <Button variant="default">Success</Button>
                <Button variant="default" disabled>
                  Loading...
                </Button>
                <Button variant="default" disabled>
                  Disabled
                </Button>
              </div>
            </div>

            {/* Inputs */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Inputs</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                <Input label="Nombre" placeholder="Ingresa tu nombre" />
                <Input label="Email" type="email" placeholder="tu@email.com" />
                <Input label="Con error" error="Este campo es requerido" />
                <Input label="Con helper" helperText="Este es un texto de ayuda" />
              </div>
            </div>

            {/* Textarea and Checkbox */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
              <Textarea label="Mensaje" placeholder="Escribe un mensaje..." rows={4} />
              <div className="space-y-2">
                <Checkbox label="Acepto los términos y condiciones" />
                <Checkbox label="Suscribirme al newsletter" />
                <Checkbox label="Recordarme" />
              </div>
            </div>
          </div>
        </section>

        {/* Dialog & Toast */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">
              2
            </span>
            Dialog & Toast (Notificaciones)
          </h2>

          <div className="space-y-4">
            {/* Dialog */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Dialogs</h3>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setDialogOpen(true)}>Abrir Dialog Simple</Button>
                <Button onClick={() => setConfirmDialogOpen(true)} variant="destructive">
                  Abrir Confirm Dialog
                </Button>
              </div>

              <Dialog open={dialogOpen} onOpenChange={() => setDialogOpen(false)}>
                <p className="text-gray-700">
                  Este es el contenido del dialog. Puedes cerrar presionando ESC, haciendo clic
                  fuera, o en el botón X.
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button variant="default" onClick={() => setDialogOpen(false)}>
                    Aceptar
                  </Button>
                </div>
              </Dialog>

              <ConfirmDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() =>
                {
                  toast.success('Confirmado!');
                  setConfirmDialogOpen(false);
                }}
                title="Confirmar Acción"
                description="¿Estás seguro de que deseas realizar esta acción? Esta operación no se puede deshacer."
                confirmText="Sí, eliminar"
                cancelText="Cancelar"
                confirmVariant="destructive"
              />
            </div>

            {/* Toast */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Toast Notifications</h3>
              <div className="flex flex-wrap gap-2">
                <Button onClick={showSuccessToast} variant="default">
                  Success Toast
                </Button>
                <Button onClick={showErrorToast} variant="destructive">
                  Error Toast
                </Button>
                <Button onClick={showWarningToast} variant="secondary">
                  Warning Toast
                </Button>
                <Button onClick={showInfoToast} variant="outline">
                  Info Toast
                </Button>
                <Button onClick={showActionToast} variant="default">
                  Toast con Acción
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* ImageViewer */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-sm font-bold">
              3
            </span>
            Image Viewer
          </h2>

          <p className="text-gray-600 mb-4">
            Visor de imágenes con zoom, rotación, navegación y thumbnails. Usa las flechas del
            teclado o los botones para navegar.
          </p>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {sampleImages.map((image, index) => (
              <button
                key={image.AttachmentId}
                onClick={() =>
                {
                  setCurrentImageIndex(index);
                  setImageViewerOpen(true);
                }}
                className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-colors group"
              >
                <img src={image.Url} alt={image.FileName} className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
              </button>
            ))}
          </div>

          <ImageViewer
            open={imageViewerOpen}
            onClose={() => setImageViewerOpen(false)}
            images={sampleImages}
            currentImageIndex={currentImageIndex}
            onImageChange={setCurrentImageIndex}
          />
        </section>

        {/* CopyButton */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center text-sm font-bold">
              4
            </span>
            Copy to Clipboard
          </h2>

          <div className="space-y-4">
            {/* CopyButton variants */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Copy Buttons</h3>
              <div className="flex flex-wrap gap-2 items-center">
                <CopyButton text="Texto copiado al portapapeles!" variant="icon" />
                <CopyButton text="Texto para copiar" variant="button" buttonText="Copiar" />
                <span className="text-sm text-gray-500">← Haz clic para copiar</span>
              </div>
            </div>

            {/* CopyInput */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Copy Input</h3>
              <div className="max-w-2xl">
                <CopyInput
                  label="URL para compartir"
                  value="https://corgee.com/projects/123/dashboard"
                  onCopy={() => toast.success('URL copiada!', '¡Copiado!')}
                />
              </div>
            </div>

            {/* CopyCode */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Copy Code Block</h3>
              <CopyCode code={sampleCode} language="tsx" />
            </div>
          </div>
        </section>

        {/* Infinite Scroll */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-sm font-bold">
              5
            </span>
            Infinite Scroll
          </h2>

          <p className="text-gray-600 mb-4">
            Hook para implementar scroll infinito. Desplázate hacia abajo para cargar más elementos
            automáticamente.
          </p>

          <div className="border border-gray-200 rounded-lg max-h-[400px] overflow-y-auto p-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {items.map(item => (
                <div
                  key={item}
                  className="aspect-square bg-gradient-to-br from-blue-400 to-purple-500 rounded-lg flex items-center justify-center text-white font-bold text-2xl shadow-md"
                >
                  {item}
                </div>
              ))}
            </div>

            {isLoadingMore && (
              <div className="mt-4 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            )}

            {/* Sentinel element for infinite scroll */}
            <div ref={loadMoreRef} className="h-10" />
          </div>

          <p className="text-sm text-gray-500 mt-2">Total de elementos cargados: {items.length}</p>
        </section>

        {/* FileUpload */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center text-sm font-bold">
              6
            </span>
            File Upload
          </h2>

          <p className="text-gray-600 mb-4">
            Componente para cargar archivos con drag & drop, progress bar y validación de tamaño.
          </p>

          <div className="max-w-2xl">
            <FileUpload
              onFilesChange={(files: any) =>
              {
                setUploadedFiles(files);
                if (files.length > 0)
                {
                  const lastFile = files[files.length - 1];
                  if (lastFile.uploadSuccess)
                  {
                    toast.success(`Archivo "${lastFile.description}" subido correctamente`);
                  }
                  else if (lastFile.uploadSuccess === false)
                  {
                    toast.error(`Error al subir "${lastFile.description}": ${lastFile.error}`);
                  }
                }
              }}
              multiple={true}
              maxFileSize={5242880} // 5MB
            />

            {uploadedFiles.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium text-gray-700 mb-2">
                  Archivos cargados ({uploadedFiles.filter(f => f.uploadSuccess).length}/
                  {uploadedFiles.length}):
                </h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {uploadedFiles.map(file => (
                    <li key={file.id} className="flex items-center gap-2">
                      <span
                        className={
                          file.uploadSuccess
                            ? 'text-green-600'
                            : file.uploadSuccess === false
                              ? 'text-red-600'
                              : 'text-gray-400'
                        }
                      >
                        {file.uploadSuccess ? '✓' : file.uploadSuccess === false ? '✗' : '⏳'}
                      </span>
                      <span>{file.description}</span>
                      <span className="text-gray-400">
                        ({(file.fileSize / 1024).toFixed(0)} KB)
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>

        {/* ActionMenu */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-teal-100 text-teal-600 rounded-full flex items-center justify-center text-sm font-bold">
              7
            </span>
            Action Menu
          </h2>

          <p className="text-gray-600 mb-4">
            Menú contextual desplegable para mostrar acciones en conversaciones y respuestas.
            Soporta hover y click.
          </p>

          <div className="space-y-6">
            {/* ActionMenu básico */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Action Menu Básico</h3>
              <div className="flex items-center gap-4">
                <div className="p-4 bg-gray-50 rounded-lg inline-flex items-center gap-2">
                  <span className="text-sm text-gray-600">Pasa el mouse sobre el icono →</span>
                  <ActionMenu
                    items={[
                      {
                        id: 'edit',
                        label: 'Editar',
                        icon: 'icon-pencil-icon',
                        onClick: () => toast.info('Editar clickeado'),
                      },
                      {
                        id: 'delete',
                        label: 'Eliminar',
                        icon: 'icon-trash-icon',
                        onClick: () => toast.error('Eliminar clickeado'),
                        variant: 'danger',
                      },
                      {
                        id: 'share',
                        label: 'Compartir',
                        icon: 'material-icons',
                        onClick: () => toast.info('Compartir clickeado'),
                      },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* ConversationActionMenu */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Conversation Action Menu</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-800">Ejemplo de Conversación</h4>
                    <p className="text-sm text-gray-600">
                      Esta es una conversación de prueba con acciones contextuales
                    </p>
                  </div>
                  <ConversationActionMenu
                    conversation={{
                      ConversationId: 123,
                      Title: 'Conversación de prueba',
                      Body: 'Contenido de la conversación',
                      ProjectId: 1,
                      ConversationLayers: [],
                      Attachments: [],
                    }}
                    onMarkRedflag={() => toast.warning('Marcado como Redflag')}
                    onEdit={() => toast.info('Editar conversación')}
                    onDelete={() => toast.error('Eliminar conversación')}
                    onInfoWindow={() => toast.info('Abrir Info Window')}
                    onCreateItem={(item: any) => toast.success(`Crear ${item.Text}`)}
                    sideMenuItems={[
                      { ConfigValueID: 2, Text: 'Tarea', Icon: 'icon-check-icon' },
                      { ConfigValueID: 3, Text: 'Nota', Icon: 'icon-note-icon' },
                    ]}
                  />
                </div>
              </div>
            </div>

            {/* ReplyActionMenu */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Reply Action Menu</h3>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-medium text-gray-800">Respuesta de ejemplo</h4>
                    <p className="text-sm text-gray-600">
                      Esta es una respuesta con acciones para favoritos, editar y eliminar
                    </p>
                  </div>
                  <ReplyActionMenu
                    reply={{
                      ReplyId: 456,
                      ConversationId: 123,
                      Body: 'Contenido de la respuesta',
                      IsFavorite: false,
                      ReplyLayers: [],
                      Attachments: [],
                    }}
                    onMarkFavorite={() => toast.success('Marcado como favorito ⭐')}
                    onMarkUnfavorite={() => toast.info('Desmarcado de favoritos')}
                    onEdit={() => toast.info('Editar respuesta')}
                    onDelete={() => toast.error('Eliminar respuesta')}
                    onRepost={() => toast.info('Repostear')}
                    onCreateItem={(item: any) => toast.success(`Crear ${item.Text}`)}
                    sideMenuItems={[{ ConfigValueID: 2, Text: 'Tarea', Icon: 'icon-check-icon' }]}
                  />
                </div>
              </div>
            </div>

            {/* Variantes de posición */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Posiciones del Menú</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-sm text-gray-600 mb-2">Bottom Left</p>
                  <ActionMenu
                    placement="bottom-left"
                    items={[
                      { id: 'action1', label: 'Acción 1', onClick: () => {} },
                      { id: 'action2', label: 'Acción 2', onClick: () => {} },
                    ]}
                  />
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-sm text-gray-600 mb-2">Bottom Right</p>
                  <ActionMenu
                    placement="bottom-right"
                    items={[
                      { id: 'action1', label: 'Acción 1', onClick: () => {} },
                      { id: 'action2', label: 'Acción 2', onClick: () => {} },
                    ]}
                  />
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-sm text-gray-600 mb-2">Top Left</p>
                  <ActionMenu
                    placement="top-left"
                    items={[
                      { id: 'action1', label: 'Acción 1', onClick: () => {} },
                      { id: 'action2', label: 'Acción 2', onClick: () => {} },
                    ]}
                  />
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <p className="text-sm text-gray-600 mb-2">Top Right</p>
                  <ActionMenu
                    placement="top-right"
                    items={[
                      { id: 'action1', label: 'Acción 1', onClick: () => {} },
                      { id: 'action2', label: 'Acción 2', onClick: () => {} },
                    ]}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* UndoToast */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center text-sm font-bold">
              8
            </span>
            Undo Toast
          </h2>

          <p className="text-gray-600 mb-4">
            Toast con funcionalidad de deshacer para operaciones de eliminación. Muestra un
            temporizador y permite cancelar la acción.
          </p>

          <div className="space-y-6">
            {/* useUndoToast hook */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Uso del Hook useUndoToast</h3>
              <div className="space-y-2">
                <Button
                  variant="destructive"
                  onClick={() =>
                  {
                    const itemName = 'Documento importante';
                    showUndoToast({
                      message: `"${itemName}" eliminado`,
                      onUndo: () =>
                      {
                        toast.success('Eliminación cancelada', 'Restaurado');
                      },
                      onConfirm: () =>
                      {
                        toast.info('Elemento eliminado permanentemente');
                      },
                      duration: 10000, // 10 segundos para demo (en producción sería 60000)
                    });
                  }}
                >
                  Eliminar con Undo (Hook)
                </Button>
                <p className="text-sm text-gray-500">
                  Usa el hook useUndoToast integrado con el sistema de Toast existente
                </p>
              </div>
            </div>

            {/* UndoDeleteToast component */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Componente UndoDeleteToast</h3>
              <div className="space-y-2">
                <Button
                  variant="destructive"
                  onClick={() =>
                  {
                    setDeletedItem('Elemento de prueba');
                    setUndoToastVisible(true);
                  }}
                >
                  Eliminar con Undo (Componente)
                </Button>
                <p className="text-sm text-gray-500">
                  Usa el componente standalone UndoDeleteToast con barra de progreso
                </p>
              </div>

              <UndoDeleteToast
                isVisible={undoToastVisible}
                message={deletedItem ? `"${deletedItem}" eliminado` : 'Elemento eliminado'}
                duration={10000} // 10 segundos para demo
                onUndo={() =>
                {
                  setUndoToastVisible(false);
                  toast.success('Eliminación cancelada', 'Restaurado');
                }}
                onClose={() =>
                {
                  setUndoToastVisible(false);
                  toast.info('Elemento eliminado permanentemente');
                }}
              />
            </div>

            {/* Ejemplo de múltiples toasts */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Múltiples Toasts Apilados</h3>
              <div className="space-y-2">
                <Button
                  variant="secondary"
                  onClick={() =>
                  {
                    const items = ['Item 1', 'Item 2', 'Item 3'];
                    items.forEach((item, index) =>
                    {
                      setTimeout(() =>
                      {
                        showUndoToast({
                          message: `"${item}" eliminado`,
                          itemId: item,
                          onUndo: () =>
                          {
                            toast.success(`Restaurado: ${item}`);
                          },
                          onConfirm: () => {},
                          duration: 15000,
                        });
                      }, index * 500);
                    });
                  }}
                >
                  Eliminar 3 Items (Apilados)
                </Button>
                <p className="text-sm text-gray-500">
                  Los toasts se apilan automáticamente y cada uno tiene su propio temporizador
                </p>
              </div>
            </div>

            {/* Info adicional */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Características:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>✓ Temporizador configurable (default: 60 segundos)</li>
                <li>✓ Barra de progreso visual</li>
                <li>✓ Botón "Undo" para cancelar</li>
                <li>✓ Botón X para confirmar inmediatamente</li>
                <li>✓ Persistencia en localStorage</li>
                <li>✓ Apilamiento automático de múltiples toasts</li>
                <li>✓ Callbacks onUndo y onConfirm</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ImageGallery */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-pink-100 text-pink-600 rounded-full flex items-center justify-center text-sm font-bold">
              9
            </span>
            Image Gallery
          </h2>

          <p className="text-gray-600 mb-4">
            Galería de imágenes avanzada con vista de cuadrícula, favoritos, descarga múltiple y más
            funcionalidades.
          </p>

          <div className="space-y-6">
            {/* Gallery preview */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Vista Previa de Galería</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {galleryImages.slice(0, 6).map((image, _index) => (
                  <button
                    key={image.AttachmentId}
                    onClick={() => setGalleryOpen(true)}
                    className="relative aspect-square rounded-lg overflow-hidden border-2 border-gray-200 hover:border-blue-500 transition-colors group"
                  >
                    <img
                      src={image.Url}
                      alt={image.FileName}
                      className="w-full h-full object-cover"
                    />
                    {image.Favorite && (
                      <div className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center">
                        <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                      <ImageIcon className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </button>
                ))}
              </div>
              <Button
                variant="default"
                onClick={() => setGalleryOpen(true)}
                className="mt-4 w-full"
              >
                Abrir Galería Completa ({galleryImages.length} imágenes)
              </Button>
            </div>

            {/* Features list */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Características:</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Vista de cuadrícula (grid view)</li>
                  <li>✓ Vista de imagen única con zoom</li>
                  <li>✓ Toggle entre vistas</li>
                  <li>✓ Marcar/desmarcar favoritos</li>
                  <li>✓ Indicador visual de favoritos</li>
                </ul>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>✓ Descargar imagen individual</li>
                  <li>✓ Descargar todas las imágenes</li>
                  <li>✓ Copiar link al portapapeles</li>
                  <li>✓ Abrir en nueva pestaña</li>
                  <li>✓ Responsive design</li>
                </ul>
              </div>
            </div>
          </div>

          {/* ImageGallery component */}
          <ImageGallery
            open={galleryOpen}
            onClose={() => setGalleryOpen(false)}
            images={galleryImages}
            onToggleFavorite={(image: any, isFavorite: any) =>
            {
              setGalleryImages(prev =>
                prev.map(img =>
                  img.AttachmentId === image.AttachmentId ? { ...img, Favorite: isFavorite } : img,
                ),
              );
            }}
            onDownload={(image: any) =>
            {
              toast.info(`Downloading: ${image.FileName}`);
            }}
            onDownloadAll={(images: any) =>
            {
              toast.success(`Downloading ${images.length} images`);
            }}
            showFavoriteButton
            showDownloadButtons
            showGridToggle
          />
        </section>

        {/* FilterBar */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-cyan-100 text-cyan-600 rounded-full flex items-center justify-center text-sm font-bold">
              10
            </span>
            Filter Bar
          </h2>

          <p className="text-gray-600 mb-4">
            Barra de filtros con búsqueda, selector de filtros y chips para gestionar filtros
            activos.
          </p>

          <div className="space-y-6">
            {/* FilterBar example */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Filtro Interactivo</h3>
              <FilterBar
                filterOptions={sampleFilterOptions}
                activeFilters={filters}
                onFiltersChange={(newFilters: any) =>
                {
                  toast.info(`Filters changed: ${newFilters.length} active`);
                }}
                onSearch={(text: any) =>
                {
                  setSearchText(text);
                  if (text)
                  {
                    toast.info(`Searching: ${text}`);
                  }
                }}
                showSearch
                showFilters
              />
            </div>

            {/* Active filters display */}
            {filters.length > 0 && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <h4 className="font-medium text-blue-900 mb-2">Active Filters:</h4>
                <div className="flex flex-wrap gap-2">
                  {filters.map((filter: any) => (
                    <span
                      key={filter.id}
                      className="px-3 py-1 bg-blue-600 text-white text-sm rounded-full"
                    >
                      {filter.name}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Features */}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
              <h4 className="font-medium text-gray-700 mb-2">Características:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>✓ Búsqueda de texto con clear button</li>
                <li>✓ Selector de filtros dropdown</li>
                <li>✓ Chips/tags para filtros activos</li>
                <li>✓ Remover filtros individuales</li>
                <li>✓ Toggle expandir/contraer</li>
                <li>✓ Indicador de cantidad de filtros</li>
                <li>✓ Show more/less para muchos filtros</li>
              </ul>
            </div>
          </div>
        </section>

        {/* ReminderPicker */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-sm font-bold">
              11
            </span>
            Reminder Picker
          </h2>

          <p className="text-gray-600 mb-4">
            Modal para visualizar y gestionar recordatorios con tiempo relativo y opciones de
            descarte.
          </p>

          <div className="space-y-6">
            {/* Open reminder button */}
            <div>
              <h3 className="font-medium text-gray-700 mb-3">Recordatorios Activos</h3>
              <Button variant="default" onClick={() => setReminderPickerOpen(true)}>
                <Bell className="w-4 h-4" />
                Ver Recordatorios ({reminders.length})
              </Button>
            </div>

            {/* Features */}
            <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
              <h4 className="font-medium text-purple-900 mb-2">Características:</h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>✓ Modal con lista de recordatorios</li>
                <li>✓ Selección de recordatorio</li>
                <li>✓ Tiempo relativo (e.g., "in 1 hour", "30 min ago")</li>
                <li>✓ Descartar recordatorio individual</li>
                <li>✓ Descartar todos los recordatorios</li>
                <li>✓ Empty state cuando no hay recordatorios</li>
                <li>✓ Hook useReminders para gestión de estado</li>
              </ul>
            </div>
          </div>

          {/* ReminderPicker component */}
          <ReminderPicker
            open={reminderPickerOpen}
            onClose={() => setReminderPickerOpen(false)}
            reminders={reminders}
            onReminderClick={(reminder: any) =>
            {
              toast.info(`Clicked: ${reminder.Title}`);
            }}
            onDismiss={(reminder: any) =>
            {
              removeReminder(reminder.id);
              toast.success(`Dismissed: ${reminder.Title}`);
            }}
            onDismissAll={() =>
            {
              removeAll();
              setReminderPickerOpen(false);
              toast.success('All reminders dismissed');
            }}
            showDismissButtons
          />
        </section>

        {/* Resumen */}
        <section className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl shadow-md p-6 border border-blue-200">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">
            🎉 Fase 2: Componentes Completados (11/11 - 100%)
          </h2>

          <div className="space-y-4">
            <div className="text-center p-6 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-300">
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-2xl font-bold text-green-800 mb-2">¡Fase 2 Completada!</h3>
              <p className="text-green-700">
                Todos los componentes Core UI han sido migrados exitosamente
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <h3 className="font-medium text-gray-700">✅ Componentes Base UI:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Dialog - Modal base y ConfirmDialog</li>
                  <li>• Button - Variantes y IconButton</li>
                  <li>• Input - Input, Textarea, Checkbox</li>
                  <li>• Toast - Sistema de notificaciones</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium text-gray-700">✅ Componentes Avanzados:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• ImageViewer - Visor con zoom/rotación</li>
                  <li>• ImageGallery - Galería con grid view</li>
                  <li>• CopyButton - Copiar al portapapeles</li>
                  <li>• FileUpload - Drag & drop upload</li>
                  <li>• ActionMenu - Menú contextual</li>
                  <li>• UndoToast - Toast con deshacer</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium text-gray-700">✅ Hooks Personalizados:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• useInfiniteScroll - Scroll infinito</li>
                  <li>• useFileUpload - Upload de archivos</li>
                  <li>• useUndoToast - Toast con undo</li>
                  <li>• useFilters - Gestión de filtros</li>
                  <li>• useReminders - Gestión de recordatorios</li>
                </ul>
              </div>

              <div className="space-y-2">
                <h3 className="font-medium text-gray-700">✅ Utilidades:</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• FilterBar - Búsqueda y filtros</li>
                  <li>• ReminderPicker - Modal de recordatorios</li>
                  <li>• CopyInput - Input con copiar</li>
                  <li>• CopyCode - Code block con copiar</li>
                  <li>• FileItem - Componente de archivo</li>
                </ul>
              </div>
            </div>

            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <h4 className="font-medium text-yellow-900 mb-2">📝 Nota:</h4>
              <p className="text-sm text-yellow-800">
                <strong>NotificationCenter</strong> queda pendiente por su alta complejidad y
                dependencia de APIs backend específicas. Se recomienda desarrollarlo en{' '}
                <strong>Fase 13 (Real-time Features)</strong> junto con la integración completa de
                Socket.IO.
              </p>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 text-center text-sm text-gray-500">
          Corgee © CGBOT {new Date().getFullYear()} - Test de Componentes Migrados
        </div>
      </footer>
    </div>
  );
};
